<template>
  <div class="tsk-syringe-page">
    <HeroIntro />

    <section class="product-hero content-width">
      <picture>
        <source media="(max-width: 767px)" :srcset="heroPhone" />
        <img :src="heroWeb" alt="TSK Syringe" class="product-hero__image" />
      </picture>
      <div class="product-hero__copy">
        <h1 v-html="$t('TSKSyringe.AppShell.s1.title')"></h1>
        <p class="product-hero__name" v-html="$t('TSKSyringe.AppShell.s1.body1')"></p>
        <p class="product-hero__registration">{{ $t('TSKSyringe.registrationNo') }}</p>
      </div>
    </section>

    <section class="feature-section">
      <div class="section-inner">
        <h2 class="outlined-heading" v-html="$t('TSKSyringe.AppShell.s2.title')"></h2>
        <div class="feature-grid">
          <article v-for="item in features" :key="item.title" class="feature-card">
            <img :src="item.image" :alt="$t(item.title)" loading="lazy" />
            <h3 v-html="$t(item.title)"></h3>
            <p v-html="$t(item.body)"></p>
          </article>
        </div>
      </div>
    </section>

    <section class="story-section content-width">
      <article class="story-row">
        <div class="story-copy">
          <h2>0.7 ml / 1.0 ml {{ localizedChoiceTitle }}</h2>
          <p class="story-lead">{{ localizedChoiceLead }}</p>
          <ul>
            <li>{{ localizedChoicePoint1 }}</li>
            <li>{{ localizedChoicePoint2 }}</li>
            <li>{{ localizedChoicePoint3 }}</li>
          </ul>
        </div>
        <div class="story-media story-media--injection">
          <img src="/TSY_NEW/TSY_injection.png" alt="TSK Syringe injection control" loading="lazy" />
        </div>
      </article>

      <article class="story-row story-row--reverse">
        <div class="story-copy">
          <h2>{{ localizedScaleTitle }}</h2>
          <p class="story-lead">{{ localizedScaleLead }}</p>
          <ul>
            <li>{{ localizedScalePoint1 }}</li>
            <li>{{ localizedScalePoint2 }}</li>
            <li>{{ localizedScalePoint3 }}</li>
          </ul>
        </div>
        <div class="story-media story-media--scale">
          <video class="story-scale-desktop" autoplay muted loop playsinline preload="metadata" aria-label="TSK Syringe extended scale demonstration">
            <source src="/TSY_NEW/TSY_injection.mp4" type="video/mp4" />
          </video>
          <Rule v-if="isMobileViewport" class="story-rule-mobile" />
        </div>
      </article>

      <article class="story-row">
        <div class="story-copy">
          <h2>MAXIMUM USAGE, MINIMAL WASTE</h2>
          <p class="story-lead">{{ localizedLowDeadSpaceLead }}</p>
          <p>{{ localizedLowDeadSpaceBody }}</p>
        </div>
        <div class="story-media story-media--cost">
          <video autoplay muted loop playsinline preload="metadata" aria-label="TSK Syringe low dead space comparison">
            <source src="/TSY_NEW/TSY_cost.mp4" type="video/mp4" />
          </video>
        </div>
      </article>

      <article class="compatibility-block">
        <h2 v-html="compatibilityTitle"></h2>
        <p v-html="compatibilityBody"></p>
        <a
          v-if="isTraditionalChinese || isEnglish"
          class="compatibility-link"
          href="/product/TskSelectNeedle"
          v-html="$t('TSKSyringe.public.compatibilityLink')"
        ></a>
        <video autoplay muted loop playsinline preload="metadata" aria-label="TSK Select Needle and TSK Syringe compatibility">
          <source src="/TSY_NEW/TSY_LDS.mp4" type="video/mp4" />
        </video>
      </article>
    </section>

    <Fin />

    <section class="youtube-section">
      <div class="video-frame">
        <iframe
          src="https://www.youtube.com/embed/25ckP0gxraU"
          title="TSK Syringe video"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
    </section>

    <section class="size-section content-width">
      <div class="size-visual">
        <img :src="sizeImage" alt="TSK Syringe 0.7 ml and 1.0 ml" loading="lazy" />
        <div class="size-copy size-copy--left">
          <span>0.7 ml</span>
          <p v-html="$t('TSKSyringe.AppShell.s7.07')"></p>
        </div>
        <div class="size-copy size-copy--right">
          <span>1.0 ml</span>
          <p v-html="$t('TSKSyringe.AppShell.s7.10')"></p>
        </div>
      </div>

      <div class="legal-copy">
        <p>{{ $t('TSKSyringe.usage') }}</p>
        <p>{{ $t('TSKSyringe.registrationNo') }}</p>
      </div>

      <a class="catalogue-button" href="/Brochure/TSK_Syringe.pdf" target="_blank" rel="noopener noreferrer">
        Download the catalogue
      </a>
    </section>

    <ContactFooter />
  </div>
</template>

<script>
import HeroIntro from './TSKSyringe/HeroIntro.vue'
import Fin from './TSKSyringe/Fin.vue'
import Rule from './TSKSyringe/Rule.vue'
import ContactFooter from '@/components/ContactFooter.vue'

export default {
  name: 'PublicTSKSyringe',
  components: { HeroIntro, Fin, Rule, ContactFooter },
  data () {
    return {
      isMobileViewport: false,
      features: [
        {
          image: require('@/assets/images/AppShell/products/TSY/web/TSY_02_01.png'),
          title: 'TSKSyringe.AppShell.s2.title1',
          body: 'TSKSyringe.AppShell.s2.body1'
        },
        {
          image: require('@/assets/images/AppShell/products/TSY/web/TSY_02_02.png'),
          title: 'TSKSyringe.AppShell.s2.title2',
          body: 'TSKSyringe.AppShell.s2.body2'
        },
        {
          image: require('@/assets/images/AppShell/products/TSY/web/TSY_02_03.png'),
          title: 'TSKSyringe.AppShell.s2.title3',
          body: 'TSKSyringe.AppShell.s2.body3'
        },
        {
          image: require('@/assets/images/AppShell/products/TSY/web/TSY_02_04.png'),
          title: 'TSKSyringe.AppShell.s2.title4',
          body: 'TSKSyringe.AppShell.s2.body4'
        }
      ]
    }
  },
  mounted () {
    this.updateMobileViewport()
    window.addEventListener('resize', this.updateMobileViewport)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.updateMobileViewport)
  },
  computed: {
    heroWeb () {
      return require('@/assets/images/AppShell/products/TSY/web/TSY_01.png')
    },
    heroPhone () {
      return require('@/assets/images/AppShell/products/TSY/phone/TSY_01.jpg')
    },
    sizeImage () {
      return require('@/assets/images/AppShell/products/TSY/web/TSY_05.png')
    },
    isEnglish () {
      return String(this.$i18n.locale || '').toLowerCase().startsWith('en')
    },
    isTraditionalChinese () {
      return String(this.$i18n.locale || '').toLowerCase().replace('_', '-').startsWith('zh-tw')
    },
    localizedChoiceTitle () {
      return this.isEnglish ? 'Syringe size options' : '規格選擇'
    },
    localizedChoiceLead () {
      return this.isEnglish
        ? 'Suitable for diverse injection needs with stable, controlled delivery.'
        : '適用於多元注射需求，穩定掌握推注'
    },
    localizedChoicePoint1 () {
      return this.isEnglish
        ? 'Designed and manufactured entirely in Japan under rigorous quality management'
        : '日本原廠全程設計與製造，落實嚴格品質管理'
    },
    localizedChoicePoint2 () {
      return this.isEnglish
        ? 'Crystal-clear material provides unobstructed visibility, making it easy to identify medication dosage'
        : '材質透亮清晰，視野清楚，方便辨識藥劑注射量'
    },
    localizedChoicePoint3 () {
      return this.isEnglish
        ? 'Stable push performance for different clinical injection scenarios'
        : '穩定推注表現，因應不同臨床注射情境'
    },
    localizedScaleTitle () {
      return this.isEnglish ? 'Extended scale design' : '加長刻度設計'
    },
    localizedScaleLead () {
      return this.isEnglish ? 'TSK 0.7 mL Syringe' : 'TSK 0.7mL 注射針筒'
    },
    localizedScalePoint1 () {
      return this.isEnglish
        ? 'Clear scale markings make micro-dose measurements easy to control'
        : '清晰刻度設計，方便掌握微量注射劑量'
    },
    localizedScalePoint2 () {
      return this.isEnglish
        ? 'A balanced plunger length maintains a natural range of hand movement'
        : '適中的推桿長度設計，維持自然的手部操作幅度'
    },
    localizedScalePoint3 () {
      return this.isEnglish
        ? 'Stable finger support is suitable for multi-point injections'
        : '穩定手指支撐，適合多點注射操作'
    },
    localizedLowDeadSpaceLead () {
      return this.isEnglish ? 'LOW DEAD SPACE' : '低死腔設計 LOW DEAD SPACE'
    },
    localizedLowDeadSpaceBody () {
      return this.isEnglish
        ? 'TSK Syringe features a low dead space design that reduces medication residue and waste after injection, improving medication utilization. It is especially suited to injection applications where medication use and cost management are important.'
        : 'TSK 注射針筒採用低死腔設計，減少注射後的藥劑殘留與浪費，提升藥劑使用效率，尤其適合重視藥劑使用與成本管理的注射應用。'
    },
    compatibilityTitle () {
      const title = this.isTraditionalChinese
        ? 'TSK 尖針 + 注射針筒 = 完美搭配，精準注射'
        : String(this.$t('TSKSyringe.AppShell.s3.title2'))
      return title
        .replace('=', '=<span class="compatibility-mobile-line-break"></span>')
    },
    compatibilityBody () {
      if (this.isTraditionalChinese) {
        return 'TSK SELECT™ NEEDLE 與 TSK 注射針筒皆採用低死腔設計<br>搭配使用可進一步減少藥劑殘留與浪費，提升高價值藥劑的使用效率'
      }
      return this.$t('TSKSyringe.AppShell.s3.body2')
        .replace(
        /<link>(.*?)<\/link>/g,
        '<a href="/product/TskSelectNeedle">$1</a>'
      )
    }
  },
  methods: {
    updateMobileViewport () {
      this.isMobileViewport = window.innerWidth <= 767
    }
  }
}
</script>

<style scoped>
.tsk-syringe-page {
  --purple: #9a76c6;
  --ink: #4f4f4f;
  overflow-x: hidden;
  color: var(--ink);
  background: #fff;
  font-family: Arial, 'PingFang TC', 'Noto Sans TC', sans-serif;
}

.content-width,
.section-inner {
  width: min(1320px, calc(100% - 48px));
  margin-inline: auto;
}

.tsk-syringe-page :deep(.hero-title) {
  top: 18%;
}

.tsk-syringe-page :deep(.hero-animation),
.tsk-syringe-page :deep(.fin-animation) {
  transform: scale(.9025);
  transform-origin: center;
}

.product-hero {
  position: relative;
  line-height: 0;
}

.product-hero picture,
.product-hero__image {
  display: block;
  width: 100%;
}

.product-hero__copy {
  position: absolute;
  top: 50%;
  left: 9%;
  width: 32%;
  transform: translateY(-50%);
  text-align: left;
  line-height: 1.5;
}

.product-hero__copy h1 {
  margin: 0 0 6px;
  color: #111 !important;
  font-size: clamp(24px, 2.5vw, 38px);
  font-weight: 700;
  letter-spacing: .02em;
}

.product-hero__name {
  margin: 0;
  color: #222;
  font-size: clamp(16px, 1.35vw, 22px);
}

.product-hero__registration {
  margin: 8px 0 0;
  color: #555;
  font-size: 13px;
}

.feature-section {
  width: min(1320px, calc(100% - 48px));
  margin-inline: auto;
  padding: 38px 0 54px;
  background: #f7f7f7;
}

.outlined-heading {
  width: fit-content;
  margin: 0 auto 30px;
  padding: 8px 24px;
  border: 0;
  color: var(--purple) !important;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(18px, 3vw, 48px);
}

.feature-card {
  text-align: center;
}

.feature-card img {
  width: min(100%, 170px);
  height: auto;
}

.feature-card h3 {
  margin: 10px 0 6px;
  color: #111 !important;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.45;
}

.feature-card p {
  margin: 0;
  color: #737373;
  font-size: 1rem;
  line-height: 1.9;
}

.story-section {
  padding: 66px 0 30px;
}

.story-row {
  display: grid;
  grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
  align-items: center;
  gap: clamp(42px, 7vw, 105px);
  width: min(1080px, 100%);
  margin: 0 auto 80px;
}

.story-copy {
  position: relative;
  z-index: 2;
  text-align: left;
}

.story-row--reverse .story-copy {
  grid-column: 2;
  grid-row: 1;
}

.story-row--reverse .story-media {
  grid-column: 1;
  grid-row: 1;
}

.story-copy h2,
.compatibility-block h2 {
  margin: 0 0 16px;
  color: var(--purple) !important;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
}

.story-copy p,
.story-copy li,
.compatibility-block p {
  color: #676767;
  font-size: 16px;
  line-height: 1.8;
}

.story-copy p {
  margin: 0 0 10px;
}

.story-copy .story-lead {
  color: #4a4a4a;
  font-weight: 700;
}

.story-copy ul {
  margin: 0;
  padding-left: 1.25em;
}

.story-media img,
.story-media video,
.compatibility-block video {
  display: block;
  width: 100%;
  height: auto;
  background: #fff;
}

.story-media {
  position: relative;
  z-index: 1;
}

.story-rule-mobile {
  display: none;
}

.story-media--injection {
  width: 130%;
  margin-left: -15%;
}

.story-media--scale {
  width: 150%;
  margin-left: -25%;
}

.compatibility-block {
  width: min(1320px, 100%);
  margin: 10px auto 72px;
  text-align: center;
}

.compatibility-block p {
  max-width: none;
  margin: 0 auto 22px;
  white-space: nowrap;
}

.compatibility-block :deep(a) {
  color: var(--purple);
  font-weight: 700;
  text-decoration: none;
}

.compatibility-link {
  display: inline-block;
  margin: 0 auto 22px;
}

.compatibility-block :deep(.compatibility-mobile-line-break) {
  display: none;
}

.compatibility-block video {
  width: 65%;
  margin-inline: auto;
}

.youtube-section {
  padding: 110px 24px;
  background: #f7f7f7;
}

.video-frame {
  position: relative;
  width: min(900px, 100%);
  margin: 0 auto;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #eee;
}

.video-frame iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.size-section {
  padding: 60px 0 70px;
  text-align: center;
}

.size-visual {
  position: relative;
  width: min(980px, 100%);
  margin: 0 auto;
}

.size-visual > img {
  display: block;
  width: 90%;
  height: auto;
  margin-inline: auto;
}

.size-copy {
  position: absolute;
  top: 58%;
  width: 28%;
  text-align: left;
}

.size-copy--left {
  left: 2%;
}

.size-copy--right {
  right: 1%;
}

.size-copy > span {
  display: block;
  width: 116px;
  padding: 5px 10px;
  border-radius: 999px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
}

.size-copy--left > span {
  background: #b99bd8;
}

.size-copy--right > span {
  background: #d8d95d;
}

.size-copy p {
  margin: 10px 0 0;
  color: #555;
  font-size: 14px;
  line-height: 1.65;
}

.legal-copy {
  margin: 18px auto 24px;
  color: #111;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.65;
}

.legal-copy p {
  margin: 0;
}

.catalogue-button {
  display: inline-flex;
  min-width: 300px;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  padding: 10px 34px;
  border: 2px solid var(--purple);
  border-radius: 999px;
  color: var(--purple);
  font-size: 18px;
  font-weight: 500;
  text-decoration: none;
  transition: color .2s ease, background-color .2s ease;
}

.catalogue-button:hover,
.catalogue-button:focus-visible {
  color: #fff;
  background: var(--purple);
}

@media (max-width: 899px) {
  .content-width,
  .section-inner {
    width: min(100%, calc(100% - 32px));
  }

  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 34px 18px;
  }

  .feature-section {
    width: min(100%, calc(100% - 32px));
  }

  .story-row {
    grid-template-columns: 1fr;
    gap: 26px;
    margin-bottom: 58px;
  }

  .story-row--reverse .story-copy,
  .story-row--reverse .story-media {
    grid-column: 1;
    grid-row: auto;
  }

  .story-row--reverse .story-copy {
    order: 1;
  }

  .story-row--reverse .story-media {
    order: 2;
  }

  .story-media--injection,
  .story-media--scale {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 767px) {
  .content-width,
  .section-inner {
    width: 100%;
  }

  .tsk-syringe-page :deep(.hero-intro) {
    height: min(140vw, 680px);
    align-items: flex-start;
  }

  .tsk-syringe-page :deep(.hero-animation),
  .tsk-syringe-page :deep(.hero-animation .frame-container) {
    width: 100%;
    height: 100%;
  }

  .tsk-syringe-page :deep(.hero-animation .frame-container) {
    overflow: hidden;
  }

  .tsk-syringe-page :deep(.hero-animation img) {
    width: 100%;
    height: auto;
    max-height: none;
    object-fit: contain;
    transform: translateY(-24vw);
  }

  .tsk-syringe-page :deep(.hero-title) {
    top: 14%;
  }

  .product-hero__copy {
    top: 18%;
    left: 50%;
    width: calc(100% - 40px);
    transform: translateX(-50%);
    text-align: center;
  }

  .product-hero__copy h1 {
    font-size: 27px;
  }

  .product-hero__name {
    font-size: 17px;
  }

  .product-hero__registration {
    font-size: 12px;
  }

  .feature-section {
    width: 100%;
    padding: 34px 16px 48px;
  }

  .feature-grid {
    grid-template-columns: 1fr;
    gap: 38px;
  }

  .outlined-heading {
    margin-bottom: 26px;
    padding: 7px 20px;
    font-size: 20px;
  }

  .feature-card img {
    width: min(100%, 142px);
  }

  .feature-card h3 {
    font-size: 1.25rem;
  }

  .feature-card p {
    font-size: 1rem;
  }

  .story-section {
    padding: 48px 20px 10px;
  }

  .story-copy {
    text-align: center;
  }

  .story-copy ul {
    display: inline-block;
    margin-inline: auto;
    text-align: left;
  }

  .story-media .story-scale-desktop {
    display: none;
  }

  .story-media--cost {
    overflow: hidden;
  }

  .story-media--cost video {
    width: 132%;
    max-width: none;
    margin-left: -20%;
  }

  .story-rule-mobile {
    display: flex;
    width: 100%;
    height: auto;
    aspect-ratio: 9 / 11.5;
    align-items: flex-start;
    justify-content: flex-start;
    overflow: hidden;
  }

  .story-rule-mobile :deep(.rule-title) {
    display: none;
  }

  .story-rule-mobile :deep(.rule-animation),
  .story-rule-mobile :deep(.frame-container) {
    width: 100%;
  }

  .story-rule-mobile :deep(.rule-animation img) {
    width: 100%;
    height: auto;
    max-height: none;
    transform: translateY(-30%);
  }

  .story-copy h2,
  .compatibility-block h2 {
    font-size: 24px;
  }

  .story-copy p,
  .story-copy li,
  .compatibility-block p {
    font-size: 1rem;
  }

  .compatibility-block p {
    white-space: normal;
  }

  .compatibility-block :deep(.compatibility-mobile-line-break) {
    display: block;
  }

  .compatibility-block video {
    width: 100%;
  }

  .tsk-syringe-page :deep(.fin-intro) {
    height: min(130vw, 620px);
  }

  .tsk-syringe-page :deep(.fin-animation),
  .tsk-syringe-page :deep(.fin-animation .frame-container) {
    width: 100%;
    height: 100%;
  }

  .tsk-syringe-page :deep(.fin-animation .frame-container) {
    overflow: hidden;
  }

  .tsk-syringe-page :deep(.fin-animation img) {
    width: 100%;
    height: auto;
    max-height: none;
    object-fit: contain;
    transform: translateY(-26vw);
  }

  .tsk-syringe-page :deep(.fin-title) {
    width: 100%;
    max-width: 100%;
    padding-inline: 20px;
    box-sizing: border-box;
    text-align: center;
  }

  .youtube-section {
    padding: 44px 16px;
  }

  .size-section {
    padding: 42px 16px 56px;
  }

  .size-visual {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 14px;
  }

  .size-visual > img {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .size-copy {
    position: static;
    grid-row: 1;
    width: auto;
    margin-bottom: 6px;
    text-align: center;
  }

  .size-copy--left {
    grid-column: 1;
  }

  .size-copy--right {
    grid-column: 2;
  }

  .size-copy > span {
    width: 96px;
    margin: 0 auto;
    font-size: 14px;
  }

  .size-copy p {
    font-size: 11px;
    text-align: center;
  }

  .legal-copy {
    margin-top: 16px;
    font-size: 12px;
  }

  .catalogue-button {
    width: min(100%, 320px);
    min-width: 0;
    font-size: 16px;
  }
}
</style>
